import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup, ValidationErrors, ValidatorFn, UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { sortBy } from 'lodash';
import { TabStripComponent } from '@progress/kendo-angular-layout';
import { TextProcessor } from 'src/app/core/utility/text.processor';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { ExtensionService } from 'src/app/core/service/extension.service';
import { SettingService } from 'src/app/core/service/setting.service';

const swapArrayLocs = (arr: [], index1: number, index2: number) => { [arr[index1], arr[index2]] = [arr[index2], arr[index1]]; };

@Component({
  selector: 'app-formly-designer-field-edit',
  templateUrl: './formly-designer-field-edit.component.html',
  styleUrls: ['./formly-designer-field-edit.component.scss']
})
export class FormlyDesignerFieldEditComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;
  @ViewChild('fieldKeyInput') public fieldKeyInput: ElementRef;

  @Input() public isNew = false;
  @Input() public isCopy = false;
  @Input() public usedKeys: string[];
  @Input() public fields: FormlyFieldConfig[];
  @Input() public consentTypeAssessment = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public apply: EventEmitter<FormlyFieldConfig> = new EventEmitter();

  public hiddenOptionRequired = true;
  public formKey: any;
  public editForm: any;
  public editFormActive = false;
  public inputTypes: Array<{ text: string; id: string }> = [
    { text: 'Text', id: 'text' },
    { text: 'Number', id: 'number' },
    { text: 'Email', id: 'email' }
  ];

  public availableTabs: Array<{ text: string; id: string }> = [];
  public selectedTab = null;
  public codeSets: any[] = [];
  public consentableDataTypes = [];
  public patientExtensions: any[] = [];
  public caseExtensions: any[] = [];
  public patientLabel = {};

  constructor(
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private dictionaryService: DictionaryService,
    private messageHandlerService: MessageHandlerService,
    private extensionService: ExtensionService,
    private settingService: SettingService,
    private logger: NGXLogger
  ) { }

  public get templateOptions(): UntypedFormControl
  {
    return this.editForm !== undefined ? this.editForm.get('templateOptions') : null;
  }

  public get options(): UntypedFormControl
  {
    return this.editForm !== undefined ? this.editForm.get('templateOptions').get('options') : null;
  }

  public get type(): UntypedFormControl
  {
    return this.editForm !== undefined ? this.editForm.get('type') : null;
  }

  public get key(): UntypedFormControl
  {
    return this.editForm !== undefined ? this.editForm.get('key') : null;
  }

  public get tab(): UntypedFormControl
  {
    return this.editForm !== undefined ? this.editForm.get('tab') : null;
  }

  public get expressionProperties(): UntypedFormControl
  {
    return this.editForm.get('expressionProperties');
  }

  public get className(): UntypedFormControl
  {
    return this.editForm.get('templateOptions').get('className');
  }

  public get sumKey(): UntypedFormControl
  {
    return this.editForm.get('templateOptions').get('sumKey');
  }

  public get externalId(): UntypedFormControl
  {
    return this.editForm.get('templateOptions').get('externalId');
  }

  public get codeSetId(): UntypedFormControl
  {
    return this.editForm.get('templateOptions').get('codeSetId');
  }

  public get savePatientExtensionId(): UntypedFormControl
  {
    return this.editForm.get('templateOptions').get('savePatientExtensionId');
  }

  public get saveCaseExtensionId(): UntypedFormControl
  {
    return this.editForm.get('templateOptions').get('saveCaseExtensionId');
  }

  public get consentDictionaryId(): UntypedFormControl
  {
    return this.editForm.get('templateOptions').get('consentDictionaryId');
  }

  @Input() public set model(field: FormlyFieldConfig)
  {
    if (field === undefined)
    {
      return;
    }

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.extensionService.getAllOrganizationExtensions().subscribe({
          next: (data) =>
          {
            this.patientExtensions = sortBy(data[0], ['name']);
            this.caseExtensions = data[1];

            // Loading consentable data types
            if (this.consentTypeAssessment) 
            {
              this.dictionaryService.getCachedConsentableDataTypes().subscribe({
                next: (data) =>
                {
                  this.consentableDataTypes = data;
                  this.initModel(field);
                }
              });
            }
            else
            {
              this.initModel(field);
            }
          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /*
  @HostListener('document:paste', ['$event'])
  public onPaste(event: ClipboardEvent, index: number)
  {
    event.preventDefault();  // Prevent default paste action

    // Get the clipboard data (text being pasted)
    const modifiedText = this.removeUnwantedChars(event.clipboardData?.getData('text') ?? '');

    // Update the form control with the modified text
    if (this.editForm?.controls?.templateOptions?.controls?.options?.controls[index]?.controls?.value)
    {
      this.editForm.controls.templateOptions.controls.options.controls[index].controls.value.setValue(modifiedText);
    }
  }*/

  public ngOnInit(): void
  {
    this.translate.get('FORM-INPUT-TYPES').subscribe({
      next: (res: string) =>
      {
        const splitted = res.split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          if (this.inputTypes.length > i)
          {
            this.inputTypes[i].text = splitted[i];
          }
        }
        this.inputTypes = sortBy(this.inputTypes, ['text']);
      }
    });
  }

  /**
   * Add an option entry
   *
   * @param label Label
   * @param value Value
   * @param externalId External Id used for SMC mapping
   */
  public addOption(label = '', value = '', externalId = ''): void
  {
    this.editForm.controls.templateOptions.controls.options.push(this.initOption(label, value, externalId));
    this.hiddenOptionRequired = true;
    this.editForm.markAsDirty();
  }

  /**
   * Remove an option entry
   *
   * @param i Index
   */
  public removeOption(i: number): void
  {
    const control = this.editForm.controls.templateOptions.controls.options;
    control.removeAt(i);
    this.editForm.markAsDirty();
  }

  /**
   * Cancel handler
   *
   * @param e Event
   */
  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Apply handler
   *
   * @param e Event
   */
  public onApply(e: Event): void
  {
    e.preventDefault();

    // Ensure we have options defined for radio, select and multi-checkbox types
    if (this.isInputAsOptions() || this.isTab())
    {
      const allowEmptyOptions = this.isSelect() && this.codeSetId.value;
      if (this.editForm.value.templateOptions.options.length === 0 && !allowEmptyOptions)
      {
        this.hiddenOptionRequired = false;
        this.tabstrip.selectTab(1);
        return;
      }
    }

    if (!(this.isDate() || this.isDateTime()) && this.editForm.value.templateOptions && this.editForm.value.templateOptions.defaultToday)
    {
      delete this.editForm.value.templateOptions.defaultToday;
    }

    // Clean up fields before applying changes.
    this.cleanFields();

    // Apply changes and close form.
    this.apply.emit(this.editForm.value);
    this.closeEditForm();
  }

  /**
   * Return true if the field is an input type.
   *
   * @returns boolean
   */
  public isInput(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('type').value === 'input';
  }

  /**
   * Return true if the input field is numeric.
   *
   * @returns boolean
   */
  public isInputNumeric(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('templateOptions').value.type === 'numeric';
  }

  /**
   * Return true if the input field is a header.
   *
   * @returns boolean
   */
  public isInputHeader(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('templateOptions').value.type === 'header';
  }

  /**
   * Return true if it is a checkbox type.
   *
   * @returns boolean
   */
  public isCheckbox(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('type').value === 'checkbox';
  }

  /**
   * Return true if it is a radio type.
   *
   * @returns boolean
   */
  public isRadio(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('type').value === 'radio';
  }

  /**
   * Return true if it is a select type.
   *
   * @returns boolean
   */
  public isSelect(): boolean
  {
    return this.editForm !== undefined && (this.editForm.get('type').value === 'select' || this.editForm.get('type').value === 'multiselect');
  }

  /**
   * Return true if it is a multi-checkbox type.
   *
   * @returns boolean
   */
  public isMultiCheckbox(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('type').value === 'multicheckbox';
  }

  /**
   * Return true if it is a date type.
   *
   * @returns boolean
   */
  public isDate(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('type').value === 'date';
  }

  /**
   * Return true if it is a date time type.
   *
   * @returns boolean
   */
  public isDateTime(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('type').value === 'datetime';
  }

  /**
   * Return true if it is a tab type.
   *
   * @returns boolean
   */
  public isTab(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('type').value === 'tabs';
  }

  /**
   * Return true if it is a label text type.
   *
   * @returns boolean
   */
  public isLabelText(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('templateOptions').value.type === 'label';
  }

  /**
   * Return true if placeholder is being used.
   *
   * @returns boolean
   */
  public showPlaceholder(): boolean
  {
    return !this.isRadio() && !this.isSelect() &&
      !this.isCheckbox() && !this.isInputHeader() && !this.isMultiCheckbox() &&
      !this.isDate() && !this.isDateTime() && !this.isTab && !this.isLabelText();
  }

  /**
   * Return true if the input field is numeric.
   *
   * @returns boolean
   */
  public isInputEmail(): boolean
  {
    return this.editForm !== undefined && this.editForm.get('templateOptions').value.type === 'email';
  }

  /**
   * Return true if the input field contains options.
   *
   * @returns boolean
   */
  public isInputAsOptions(): boolean
  {
    return this.editForm !== undefined && (this.isRadio() ||
      (this.isSelect() && !this.editForm.get('templateOptions').value.codeSetId) || this.isMultiCheckbox() || this.isTab());
  }

  /**
   * This set the class name based on the selected width 25%, 50%, 75% or 100%.
   *
   * @param className Class name
   */
  public setClassName(className: string): void
  {
    this.className.setValue(className);
  }

  /**
   * This event handle disabled the ESC keyboard key of the formly-designed-field-edit.
   *
   * @param e Event
   */
  public modalKeyDown(event: KeyboardEvent): void
  {
    if (event && event.key === 'Escape')
    {
      event.preventDefault();
    }
  }

  public validateKey(e: KeyboardEvent): void
  {
    TextProcessor.validateKeyWithUnderscoreAndDashNoSpaces(e);
    if (this.editForm.controls.key.value !== undefined)
    {
      this.editForm.controls.key.setValue(this.editForm.controls.key.value.replaceAll(/ /g, ''));   // To handle paste that contains spaces
    }
  }

  public validateValue(e: KeyboardEvent, index: number): void
  {
    TextProcessor.validateKeyWithUnderscoreAndDashNoSpaces(e);
    if (this.editForm.controls.templateOptions.controls.options.controls[index].controls.value !== undefined &&
      this.editForm.controls.templateOptions.controls.options.controls[index].controls.value.value)
    {
      this.editForm.controls.templateOptions.controls.options.controls[index].controls.value.setValue(
        this.removeUnwantedChars(this.editForm.controls.templateOptions.controls.options.controls[index].controls.value.value));
    }
  }

  public optionNameChanged(e: KeyboardEvent, index: number): void
  {
    if (this.editForm.controls.templateOptions.controls.options.controls[index].controls?.label?.value &&
      this.editForm.controls.templateOptions.controls.options.controls[index].controls?.value?.value === '')
    {
      const label = this.editForm.controls.templateOptions.controls.options.controls[index].controls?.label?.value;
      let words = label.split(' ');
      for (let i = 0; i < words.length; i++)
      {
        words[i] = words[i][0].toUpperCase() + words[i].substring(1);
      }
      words = this.removeUnwantedChars(words.join(' '));
      this.editForm.controls.templateOptions.controls.options.controls[index].controls?.value.setValue(words);
    }
  }

  /**
   * Event trigerred when the label text is changed.
   */
  public labelChange(): void
  {
    this.editForm.markAsDirty();
  }

  public changeTabOrder(index: number, direction: string): void
  {
    swapArrayLocs(this.options.value, index, direction === 'up' ? index - 1 : index + 1);
    swapArrayLocs(this.editForm.controls.templateOptions.controls.options.controls, index, direction === 'up' ? index - 1 : index + 1);
    this.editForm.markAsDirty();
  }

  /**
   * Model initilization
   *
   * @param field Formly field edit object.
   */
  private initModel(field: any): void
  {
    this.logger.info('field model', field);
    this.logger.info('fields', this.fields);
    this.formKey = field.key;

    let keyField = [];
    let typeField = [];
    if (field.type === 'tabs' || field.templateOptions.type !== 'header')
    {
      let keyFieldValidators = [];
      if (this.isNew || this.isCopy)
      {
        keyFieldValidators = [Validators.required, Validators.pattern('[a-zA-Z]*'), this.uniqueNameValidator()];
      }
      keyField = [
        { value: field.key, disabled: !(this.isNew || this.isCopy) }, keyFieldValidators
      ];

      if (field.templateOptions && field.templateOptions?.type === 'label')
      {
        typeField = [field.type];
      }
      else
      {
        typeField = [field.type, Validators.required];
      }
    }

    // Set true/false is the field should be render horizontaly
    const isFieldHorizontal = (field.wrappers !== undefined && field.wrappers.includes('form-field-horizontal'));

    // Build form
    this.editForm = this.fb.group({
      isFormFieldHorizontal: isFieldHorizontal,
      key: keyField,
      type: typeField,
      expressionProperties: [field.expressionProperties ? field.expressionProperties['templateOptions.disabled'] : ''],
      hideExpression: [field.hideExpression ? field.hideExpression : ''],
      templateOptions: this.initTemplateOption(
        field.type,
        this.isNew ? null : field.templateOptions.label,
        this.isNew ? null : field.templateOptions.placeholder,
        this.isNew ? null : field.templateOptions.description,
        field.templateOptions && field.templateOptions.type !== undefined ? field.templateOptions.type : null,
        this.isNew ? null : field.templateOptions.className,
        this.isNew ? null : field.templateOptions.required,
        this.isNew ? null : field.templateOptions.defaultToday,
        this.isNew ? null : field.templateOptions.minLength,
        this.isNew ? null : field.templateOptions.maxLength,
        this.isNew ? null : field.templateOptions.min,
        this.isNew ? null : field.templateOptions.max,
        this.isNew ? null : field.templateOptions.externalId,
        this.isNew ? null : field.templateOptions.codeSetId,
        this.isNew ? null : field.templateOptions.consentDictionaryId,
        this.isNew ? null : field.templateOptions.savePatientExtensionId,
        this.isNew ? null : field.templateOptions.saveCaseExtensionId,
        this.isNew ? null : field.templateOptions.sumKey
      ),
      fieldGroup: this.fb.array([]),
      tab: null
    });

    // Set templateOptions.options if any
    if (field.templateOptions && field.templateOptions.options)
    {
      field.templateOptions.options.forEach((o: any) =>
      {
        this.editForm.controls.templateOptions.controls.options.push(this.initOption(o.label, o.value, o.externalId));
      });
    }

    // Load available tab selection
    this.selectedTab = this.loadTabs(field);
    if (!this.isNew)
    {
      this.tab.setValue(this.selectedTab);
    }

    // Load CodeSets
    if (this.isSelect())
    {
      this.dictionaryService.getCachedCodeSets().subscribe({
        next: (data) =>
        {
          this.codeSets = data;
        },
        error: (error) =>
        {
          this.messageHandlerService.displayError(error);
        }
      });
    }
    this.editFormActive = true;

    // Set focus on the key name
    if (this.isNew || this.isCopy)
    {
      setTimeout(() =>
      {
        if (this.isCopy) { this.editForm.markAsDirty(); }
        if (this.fieldKeyInput) { this.fieldKeyInput.nativeElement.focus(); }
      });
    }
  }

  /**
   * Create a form group for the templateOptions object.
   * @param fieldType Control type
   * @param label Field label
   * @param placeholder Field placeholder
   * @param description Field description displayed below the control
   * @param type Field type
   * @param className Field Class Name
   * @param required Is field required?
   * @param defaultToday Default to today date or date/time?
   * @param minLength Field minimum length for the input text
   * @param maxLength Field maximum length for the input text
   * @param min Field minimum value for the numeric input
   * @param max Field maximum value for the numeric input
   * @param externalId External Id
   * @param codeSetId Code Set Id to use for options
   * @param consentDictionaryId Consent dictionary mapping
   * @param savePatientExtensionId Save Patient Extension Id
   * @param saveCaseExtensionId Save Case Extension Id
   * @param sumKey Sum key of the control to accumulate selection
   */
  private initTemplateOption(fieldType: string, label: string, placeholder: string, description: string, type: string, className: string,
    required: boolean, defaultToday: boolean, minLength: number, maxLength: number, min: number, max: number, externalId: string, codeSetId: string,
    consentDictionaryId: string, savePatientExtensionId: string, saveCaseExtensionId: string, sumKey: string): UntypedFormGroup
  {
    let typeValidator = [];
    if (fieldType === 'input')
    {
      typeValidator = [Validators.required];
    }
    return this.fb.group({
      type: [type, typeValidator],
      className: [className],
      label: [label, this.isTab ? [] : Validators.required],
      placeholder: [placeholder],
      description: [description],
      required: [required],
      defaultToday: [defaultToday],
      minLength: [minLength],
      maxLength: [maxLength],
      min: [min],
      max: [max],
      options: this.fb.array([]),
      externalId: [externalId],
      codeSetId: [codeSetId],
      consentDictionaryId: [consentDictionaryId],
      savePatientExtensionId: [savePatientExtensionId],
      saveCaseExtensionId: [saveCaseExtensionId],
      sumKey: [sumKey]
    });
  }

  /**
   * Create a form group option.
   *
   * @param label Option label.
   * @param value Option value.
   * @param externalId External Id used for SMC mapping
   */
  private initOption(label: string, value: string, externalId: string): UntypedFormGroup
  {
    return this.fb.group({
      label: [label, Validators.required],
      value: [value, Validators.required],
      externalId: [externalId],
    });
  }

  /**
   * Closing edit form
   */
  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  /**
   * This method clean up fields before apply changes.
   */
  private cleanFields(): void
  {
    // For checkbox, must set hideLabel = true
    this.editForm.value.templateOptions.hideLabel = this.isCheckbox();

    // Clean up built-in validators
    delete this.editForm.value.validators;

    // Handle Input field changes
    if (this.isInput())
    {
      if (this.isInputNumeric())
      {
        delete this.editForm.value.templateOptions.minLength;
        delete this.editForm.value.templateOptions.maxLength;
        if (!this.editForm.value.templateOptions.min)
        {
          delete this.editForm.value.templateOptions.min;
        }
        if (!this.editForm.value.templateOptions.max)
        {
          delete this.editForm.value.templateOptions.max;
        }
      }
      else
      {
        delete this.editForm.value.templateOptions.min;
        delete this.editForm.value.templateOptions.max;
        if (!this.editForm.value.templateOptions.minLength)
        {
          delete this.editForm.value.templateOptions.minLength;
        }
        if (!this.editForm.value.templateOptions.maxLength)
        {
          delete this.editForm.value.templateOptions.maxLength;
        }
      }
      // Add the email validator
      if (this.isInputEmail())
      {
        this.editForm.value.validators = {
          validation: ['email']
        };
      }
    }
    else
    {
      delete this.editForm.value.templateOptions.minLength;
      delete this.editForm.value.templateOptions.maxLength;
      delete this.editForm.value.templateOptions.min;
      delete this.editForm.value.templateOptions.max;
    }

    // Expression properties (supporting disabled only)
    const disabledExpression = this.editForm.value.expressionProperties;
    if (disabledExpression)
    {
      this.expressionProperties.setValue({ 'templateOptions.disabled': disabledExpression });
    }
    else
    {
      delete this.editForm.value.expressionProperties;
    }

    if (!this.editForm.value.hideExpression)
    {
      delete this.editForm.value.hideExpression;
    }

    // Work around: Key on edit is disable and it is missing from the post. Need to set the key manually.
    if (!(this.isNew || this.isCopy))
    {
      this.editForm.value.key = this.formKey;
    }

    // Initialize boolean default
    if (this.editForm.value.templateOptions)
    {
      if (this.editForm.value.templateOptions.required === null)
      {
        this.editForm.value.templateOptions.required = false;
      }
      if (this.editForm.value.templateOptions.defaultToday === null)
      {
        this.editForm.value.templateOptions.defaultToday = false;
      }
    }

    // Build template based on label provided
    if (this.isInputHeader())
    {
      this.editForm.value.template = `<h4 class="montserrat">${this.editForm.value.templateOptions.label}</h4><hr />`;
      this.editForm.value.templateOptions.type = 'header';
      delete this.editForm.value.key;
      delete this.editForm.value.type;
      delete this.editForm.value.templateOptions.className;
      delete this.editForm.value.templateOptions.sumKey;
      delete this.editForm.value.templateOptions.placeholder;
      delete this.editForm.value.templateOptions.description;
      delete this.editForm.value.templateOptions.required;
      delete this.editForm.value.templateOptions.defaultToday;
      delete this.editForm.value.templateOptions.hideLabel;
      delete this.editForm.value.templateOptions.externalId;
    }
    else
    {
      delete this.editForm.value.template;
      // Removing externalId if not used (blank)
      if (this.editForm.value.templateOptions && this.editForm.value.templateOptions.externalId === '')
      {
        delete this.editForm.value.templateOptions.externalId;
      }
    }

    // If dictionary selected, clear options
    if (this.editForm.value.templateOptions && this.codeSetId && this.codeSetId.value > 0 &&
      this.editForm.value && this.editForm.value.templateOptions && this.editForm.value.templateOptions.options &&
      this.editForm.value.templateOptions.options.length > 0)
    {
      this.editForm.value.templateOptions.options = [];
    }

    // Handle form field horizontal wrapper
    if (this.editForm.value.isFormFieldHorizontal)
    {
      if (this.editForm.value.wrappers === undefined)
      {
        this.editForm.value.wrappers = [];
      }
      this.editForm.value.wrappers.push('form-field-horizontal');
      this.editForm.value.templateOptions.hideLabel = true;
    }
    else
    {
      delete this.editForm.value.wrappers;
    }
    delete this.editForm.value.isFormFieldHorizontal;   // No need to submit, used as calculated field only.

    // Handle tabs: Must transform editForm.value.templateOptions to fieldGroup
    if (this.isTab()) 
    {
      this.editForm.value.templateOptions.options.forEach((o: any) =>
      {
        const foundIndex = this.editForm.value.fieldGroup.findIndex(x => x.key === o.value);
        if (foundIndex >= 0)
        {
          this.editForm.value.fieldGroup[foundIndex].templateOptions.label = o.label;
        }
        else
        {
          this.editForm.value.fieldGroup.push({
            key: o.value,
            templateOptions: { label: o.label }
          });
        }
      });
    }
    else
    {
      delete this.editForm.value.fieldGroup;
    }
  }

  /**
   * Validate if the key is unique.
   */
  private uniqueNameValidator(): ValidatorFn
  {
    return (control: UntypedFormGroup): ValidationErrors | null =>
    {
      const keyValue = control ? control.value : '';
      const keyFound = this.usedKeys !== undefined ? this.usedKeys.find((k: string) => k === keyValue) : undefined;
      if (keyFound !== undefined)
      {
        return {
          keyNameExists: true
        };
      }
      return null;
    };
  }

  /**
   * This method load all the tabs within the fields array.
   */
  private loadTabs(field: any): string
  {
    const availableTabs = [];
    let selectedTab = null;
    if (field.type !== 'tabs' && field.templateOptions.type !== 'header')  // For now, prevent using tab in a tab
    {
      const tabFields = this.fields.filter(x => x.type === 'tabs');
      if (tabFields)
      {
        tabFields.forEach(x => 
        {
          if (x.templateOptions && x.templateOptions.options)
          {
            x.templateOptions.options.forEach((o: any) =>
            {
              const tabData = { text: o.label, id: o.value };
              availableTabs.push(tabData);
            })
          }
          // Locate the current field in the tab
          if (x.fieldGroup && !this.isNew)
          {
            x.fieldGroup.forEach(tabGroup =>
            {
              if (tabGroup.fieldGroup)
              {
                tabGroup.fieldGroup.forEach(foundField =>
                {
                  if (foundField.key === field.key)
                  {
                    selectedTab = tabGroup.key;
                  }
                });
              }
            });
          }
        });
      }
    }
    this.availableTabs = sortBy(availableTabs, ['text']);
    return selectedTab;
  }

  // Function to remove unwanted characters
  private removeUnwantedChars(input: string): string
  {
    return input.replace(/[^a-zA-Z0-9]/g, '').replace(/ /g, '');
  }
}
